import { Marquee } from "./components/magicui/marquee";
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Skeleton } from "./components/ui/skeleton";
import type { Product } from "./modules/product/model";
import { storefrontPriceLabel } from "./modules/product/refined-storefront";
import { Link } from "react-router-dom";
import DeferredDeveloperCard from "./components/DeferredDeveloperCard";
import { ArrowRightIcon, ArrowUpRightIcon, ClockIcon } from "lucide-react";
import { Badge } from "./components/ui/badge";
import { Button } from "./components/ui/button";
import { FlipWords } from "./components/ui/flip-words";
import { Spotlight } from "./components/ui/spotlight-new";
import DeferredImage from "./components/DeferredImage";
import { AsciiModelFallback } from "./components/canvasui/AsciiModelFallback";
import gangnumPartner from "./assets/partners/gangnum.webp?no-inline";
import goodPartner from "./assets/partners/good.webp?no-inline";
import halfPartner from "./assets/partners/half.webp?no-inline";
import hopePartner from "./assets/partners/hope.webp?no-inline";
import hyperPartner from "./assets/partners/hyper.webp?no-inline";
import ktownPartner from "./assets/partners/ktown.webp?no-inline";
import privatePartner from "./assets/partners/private.webp?no-inline";
import sakuraPartner from "./assets/partners/sakura.webp?no-inline";
import skyPartner from "./assets/partners/sky.webp?no-inline";
import summerPartner from "./assets/partners/summer.webp?no-inline";
import productPlaceholder from "./assets/product-placeholder.png?no-inline";

const HERO_WORDS = ["ตั้งค่าได้ละเอียด", "อัปเดตสม่ำเสมอ", "เข้ารหัสปลอดภัย", "คู่มือชัดเจน"];
const DESKTOP_QUERY = "(min-width: 1024px)";
const RECOMMENDATION_PRELOAD_MARGIN = "600px 0px";
const PARTNERS = [
	{ src: gangnumPartner, name: "Gangnum" },
	{ src: goodPartner, name: "Good" },
	{ src: halfPartner, name: "Half City" },
	{ src: hopePartner, name: "Hope" },
	{ src: hyperPartner, name: "Hyper" },
	{ src: ktownPartner, name: "K Town" },
	{ src: privatePartner, name: "Private" },
	{ src: sakuraPartner, name: "Sakura Town" },
	{ src: skyPartner, name: "Sky City" },
	{ src: summerPartner, name: "Summer" },
];

const SERVICES = [
	{ icon: "settings.svg", title: "ปรับแต่งได้" },
	{ icon: "blig.svg", title: "อัปเดตสม่ำเสมอ" },
	{ icon: "shield.svg", title: "ระบบป้องกัน" },
	{ icon: "book.svg", title: "คู่มือพร้อมใช้" },
];

const TAGS = {
	esx: "ESX",
	qbcore: "QBCore",
	alone: "Standalone",
};

const DEVELOPERS = [
	{ name: "Errorism", avatar: "https://raw.githubusercontent.com/errorism-scripts/assets/refs/heads/main/errorism_avatar.gif?v=2" },
	{ name: "Godji (Lowliie)", avatar: "https://avatars.githubusercontent.com/u/32196545" },
	{ name: "Jin (Tak)", avatar: "https://avatars.githubusercontent.com/u/209262187" },
	{ name: "XBookbig", avatar: "https://raw.githubusercontent.com/errorism-scripts/assets/refs/heads/main/xbb_avatar.gif" },
];

const LazyAsciiModel = lazy(() =>
	import("./components/canvasui/AsciiModel").then(({ AsciiModel }) => ({ default: AsciiModel })),
);

const ModelRequestTrigger = ({ onRequest }: { onRequest: () => void }) => {
	useEffect(() => {
		let idleRequest: number | undefined;
		const scheduleTimer = window.setTimeout(() => {
			if ("requestIdleCallback" in window) {
				idleRequest = window.requestIdleCallback(onRequest, { timeout: 7000 });
				return;
			}

			onRequest();
		}, 1000);
		return () => {
			window.clearTimeout(scheduleTimer);
			if (idleRequest !== undefined) window.cancelIdleCallback(idleRequest);
		};
	}, [onRequest]);

	return null;
};

const HeroAsciiModel = () => {
	const [desktopRenderingEnabled, setDesktopRenderingEnabled] = useState(false);
	const [modelRequested, setModelRequested] = useState(false);
	const requestModel = useCallback(() => setModelRequested(true), []);

	useEffect(() => {
		const query = window.matchMedia(DESKTOP_QUERY);
		const update = () => setDesktopRenderingEnabled(query.matches);
		update();
		query.addEventListener("change", update);
		return () => query.removeEventListener("change", update);
	}, []);

	if (!desktopRenderingEnabled || !modelRequested) {
		return (
			<>
				{desktopRenderingEnabled && (
					<ModelRequestTrigger onRequest={requestModel} />
				)}
				<AsciiModelFallback src="/models/errorism-logo.glb" />
			</>
		);
	}

	return (
		<Suspense fallback={<AsciiModelFallback src="/models/errorism-logo.glb" />}>
			<LazyAsciiModel src="/models/errorism-logo.glb" className="absolute inset-0" />
		</Suspense>
	);
};

function App() {
	const [recommendProduct, setRecommendProduct] = useState<Product[]>([])
	const [recommendStatus, setRecommendStatus] = useState<"loading" | "ready" | "error">("loading")
	const [recommendRetryKey, setRecommendRetryKey] = useState(0)
	const [recommendEnabled, setRecommendEnabled] = useState(false)
	const recommendGridRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const grid = recommendGridRef.current;
		if (!grid) return;

		if (!("IntersectionObserver" in window)) {
			setRecommendEnabled(true);
			return;
		}

		const observer = new IntersectionObserver(
			([entry]) => {
				if (!entry?.isIntersecting) return;
				setRecommendEnabled(true);
				observer.disconnect();
			},
			{ rootMargin: RECOMMENDATION_PRELOAD_MARGIN },
		);

		observer.observe(grid);
		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		if (!recommendEnabled) return;

		const controller = new AbortController();

		const fetchData = async () => {
			setRecommendStatus("loading");
			try {
				const { productCatalog } = await import("./modules/product");
				const products = await productCatalog.listRecommendedProducts(controller.signal);

				if (!controller.signal.aborted) {
					setRecommendProduct(products);
					setRecommendStatus("ready");
				}
			} catch {
				if (!controller.signal.aborted) {
					setRecommendProduct([]);
					setRecommendStatus("error");
				}
			}
		};

		const timer = window.setTimeout(() => void fetchData(), 0);
		return () => {
			window.clearTimeout(timer);
			controller.abort();
		};
	}, [recommendEnabled, recommendRetryKey]);


	return (
		<div>
			<div className="relative isolate overflow-hidden">
				<section lang="th" className="relative isolate flex min-h-[28rem] overflow-hidden lg:min-h-[clamp(30rem,68svh,40rem)]">
					<div
						aria-hidden="true"
						className="absolute inset-0 bg-[#121116]"
						style={{
							backgroundImage: [
								"radial-gradient(ellipse at 9% 58%, rgb(89 66 139 / 28%), transparent 33%)",
								"radial-gradient(ellipse at 83% 58%, rgb(92 43 78 / 24%), transparent 34%)",
								"radial-gradient(ellipse at 50% -15%, rgb(83 40 101 / 22%), transparent 38%)",
							].join(","),
						}}
					/>
					<div className="absolute inset-0 bg-black/55" aria-hidden="true" />
					<Spotlight
						duration={12}
						xOffset={72}
						translateY={-440}
						gradientFirst="radial-gradient(68.54% 68.72% at 55.02% 31.46%, rgb(209 157 255 / 9%) 0, rgb(129 34 221 / 3%) 52%, transparent 80%)"
						gradientSecond="radial-gradient(50% 50% at 50% 50%, rgb(255 255 255 / 5%) 0, rgb(209 157 255 / 2%) 80%, transparent 100%)"
						gradientThird="radial-gradient(50% 50% at 50% 50%, rgb(129 34 221 / 4%) 0, transparent 100%)"
					/>
					<div className="container-wrapper relative z-10 grid w-full flex-1 items-center gap-8 px-4 py-10 sm:py-12 lg:grid-cols-[minmax(0,1.15fr)_minmax(20rem,0.85fr)] lg:gap-12 lg:py-16 xl:gap-16">
						<div className="relative z-10 flex max-w-[43rem] flex-col">
							<h1
								aria-label="สคริปต์ FiveM ที่ตั้งค่าได้ละเอียด อัปเดตสม่ำเสมอ เข้ารหัสปลอดภัย และมีคู่มือชัดเจน"
								className="max-w-[43rem] text-balance text-white"
							>
								<span aria-hidden="true">
									<span className="block text-balance text-lg font-medium leading-normal text-white/70 sm:text-2xl">สคริปต์ FiveM ที่</span>
									<span className="mt-3 block min-h-[1.2em] text-[clamp(2.25rem,4.5vw,4rem)] font-bold leading-[1.1] tracking-[-0.02em] sm:mt-5">
										<FlipWords words={HERO_WORDS} duration={2800} className="max-w-full whitespace-normal px-0 text-white sm:whitespace-nowrap" />
									</span>
								</span>
							</h1>

							<p className="mt-6 max-w-[50ch] text-pretty text-base font-normal leading-7 tracking-normal text-white/80 [-webkit-font-smoothing:antialiased] [text-rendering:optimizeLegibility] sm:mt-8 sm:text-lg sm:leading-8">
								สคริปต์ FiveM จากทีมนักพัฒนา Errorism ออกแบบให้ปลอดภัย ใช้งานง่าย และ<span className="whitespace-nowrap">ตั้งค่า</span>ได้ตามความต้องการ พร้อมอัปเดตให้รองรับเวอร์ชันล่าสุดอยู่เสมอ
							</p>

							<Button className="group mt-7 w-fit px-6 font-bold transition-[transform,box-shadow,background-color] duration-150 active:scale-[0.96] motion-reduce:transition-none motion-reduce:active:scale-100 sm:mt-9" size="xl" render={<Link to="/product" />}>
								เลือกดูสคริปต์ทั้งหมด
								<ArrowRightIcon aria-hidden="true" className="transition-transform duration-200 group-hover:translate-x-1 motion-reduce:transition-none" />
							</Button>
						</div>
						<div aria-hidden="true" data-ascii-hero className="pointer-events-none absolute inset-y-0 right-0 z-0 hidden w-[62%] opacity-30 lg:relative lg:inset-auto lg:block lg:h-full lg:min-h-[32rem] lg:w-full lg:opacity-100">
							<HeroAsciiModel />
						</div>
					</div>
				</section>

				<section aria-label="พาร์ทเนอร์ของ Errorism" className="relative overflow-hidden border-y border-white/5 bg-white/[0.01] py-3 sm:py-4">
					<Marquee aria-label="โลโก้พาร์ทเนอร์ของ Errorism เลื่อนเมาส์เหนือรายการเพื่อหยุด" pauseOnHover className="outline-none [--duration:70s] [--gap:1.5rem] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#D19DFF] sm:[--gap:2rem]">
						{PARTNERS.map((data) => (
							<div key={data.name} className="group flex h-20 w-28 shrink-0 items-center justify-center sm:h-24 sm:w-32">
								<img alt={data.name} className="h-18 w-24 object-contain opacity-[0.58] saturate-[0.8] transition-[opacity,filter,transform] duration-300 group-hover:scale-[1.04] group-hover:opacity-90 group-hover:saturate-100 motion-reduce:transition-none motion-reduce:group-hover:scale-100 sm:h-20 sm:w-28" decoding="async" fetchPriority="low" height="96" loading="lazy" src={data.src} width="96" />
							</div>
						))}
					</Marquee>
					<div aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-background via-background/85 to-transparent sm:w-28" />
					<div aria-hidden="true" className="pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-background via-background/85 to-transparent sm:w-28" />
				</section>
			</div>


			<section lang="th" aria-labelledby="recommend-heading" aria-busy={recommendStatus === "loading"} className="px-4 py-12 sm:py-16">
				<div className="container-wrapper">
					<header className="mb-6 flex items-end justify-between gap-4 sm:mb-8">
						<h2 id="recommend-heading" className="text-balance text-2xl font-bold leading-8">สคริปต์แนะนำ</h2>
						<Button variant="ghost" className="min-h-11 shrink-0 px-3 transition-[color,background-color,transform] duration-150 active:scale-[0.96] motion-reduce:transition-none motion-reduce:active:scale-100" render={<Link to="/product" />}>
							ดูทั้งหมด
							<ArrowRightIcon aria-hidden="true" />
						</Button>
					</header>

						<div ref={recommendGridRef} className="grid items-start gap-x-5 gap-y-10 sm:grid-cols-2 xl:grid-cols-3 xl:gap-x-6 xl:gap-y-12">
						{recommendStatus === "loading" ? (
							<>
								<span className="sr-only" role="status" aria-live="polite">กำลังโหลดสคริปต์แนะนำ</span>
								{Array.from({ length: 3 }, (_, index) => (
									<div key={index} aria-hidden="true" className="space-y-4">
										<Skeleton className="aspect-[2/1] w-full rounded-xl motion-reduce:animate-none" />
										<div className="space-y-2">
											<Skeleton className="h-6 w-2/3 motion-reduce:animate-none" />
											<Skeleton className="h-4 w-full motion-reduce:animate-none" />
											<Skeleton className="h-4 w-4/5 motion-reduce:animate-none" />
										</div>
									</div>
								))}
							</>
						) : recommendStatus === "error" ? (
							<div role="alert" className="flex min-h-56 flex-col items-center justify-center rounded-2xl border border-white/10 bg-card px-6 py-10 text-center shadow-[0_18px_50px_rgb(0_0_0/0.16)] sm:col-span-2 xl:col-span-3">
								<h3 className="text-balance text-xl font-bold">โหลดสคริปต์แนะนำไม่สำเร็จ</h3>
								<p className="mt-2 text-pretty text-base leading-7 text-muted-foreground">ตรวจสอบการเชื่อมต่อแล้วลองอีกครั้ง</p>
								<Button variant="outline" className="mt-5 min-h-11 transition-transform duration-150 active:scale-[0.96] motion-reduce:transition-none motion-reduce:active:scale-100" onClick={() => setRecommendRetryKey((key) => key + 1)}>ลองอีกครั้ง</Button>
							</div>
						) : recommendProduct.length === 0 ? (
							<div className="flex min-h-56 flex-col items-center justify-center rounded-2xl border border-dashed border-white/15 bg-card px-6 py-10 text-center shadow-[0_18px_50px_rgb(0_0_0/0.16)] sm:col-span-2 xl:col-span-3">
								<h3 className="text-balance text-xl font-bold">ยังไม่มีสคริปต์แนะนำ</h3>
								<p className="mt-2 text-pretty text-base leading-7 text-muted-foreground">ดูสคริปต์ที่พร้อมใช้งานทั้งหมดได้ในหน้าสินค้า</p>
								<Button className="mt-5 min-h-11 transition-[transform,box-shadow,background-color] duration-150 active:scale-[0.96] motion-reduce:transition-none motion-reduce:active:scale-100" render={<Link to="/product" />}>
									ดูสคริปต์ทั้งหมด
									<ArrowRightIcon aria-hidden="true" />
								</Button>
							</div>
						) : recommendProduct.map((product) => (
							<Link key={product.id} to={`/product/${product.id}`} className="group block min-w-0 rounded-xl outline-none ring-offset-background transition-transform duration-150 active:scale-[0.96] focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4 motion-reduce:transition-none motion-reduce:active:scale-100">
								<div className="relative isolate overflow-hidden rounded-xl bg-black shadow-[0_18px_50px_rgb(0_0_0/0.2)] outline outline-1 -outline-offset-1 outline-[oklch(1_0_0_/_0.1)]">
									<DeferredImage
										alt={`แบนเนอร์ ${product.name}`}
										className="block h-auto w-full transition-transform duration-500 ease-out group-hover:scale-[1.02] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
										decoding="async"
										fallbackSrc={productPlaceholder}
										fetchPriority="low"
										height="1143"
										loading="lazy"
										rootMargin={RECOMMENDATION_PRELOAD_MARGIN}
										src={product.bannerImage
											? `https://cdn.errorism.co/${product.name}/banners/${product.bannerImage}`
											: productPlaceholder}
										width="2032"
									/>
									<div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100 motion-reduce:transition-none" />
								</div>
								<div className="mt-4">
									<div className="flex items-start justify-between gap-4">
										<h3 className="min-w-0 text-xl font-bold uppercase leading-tight tracking-[-0.02em] [overflow-wrap:anywhere]">{product.name}</h3>
										<div className="flex shrink-0 items-center gap-2 pt-0.5">
											<span className="text-sm font-semibold tabular-nums">{storefrontPriceLabel(product)}</span>
											<ArrowUpRightIcon aria-hidden="true" className="size-4 text-muted-foreground transition-[color,transform] duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-foreground motion-reduce:transition-none" />
										</div>
									</div>
									<p className="mt-2 line-clamp-2 text-pretty text-sm leading-6 text-muted-foreground">{product.details}</p>
									<div className="mt-4 flex flex-wrap gap-1.5">
										{product.tags.map((tagData) => (
											<Badge key={tagData} size="lg" variant="outline" className="border-[#FED330]/50 bg-[#FED330]/5 text-[#FED330]">
												{TAGS[tagData as keyof typeof TAGS] || tagData}
											</Badge>
										))}
									</div>
								</div>
							</Link>
						))}
					</div>
				</div>
			</section>



			<section lang="th" aria-labelledby="upcoming-heading" className="px-4 pb-20 pt-4 sm:pb-24">
				<div className="container-wrapper">
					<header className="mb-6 flex flex-col gap-3 border-b border-white/10 pb-5 sm:flex-row sm:items-end sm:justify-between">
						<div>
							<h2 id="upcoming-heading" className="text-balance text-2xl font-bold">หมวดที่กำลังพัฒนา</h2>
							<p className="mt-1 text-pretty text-sm leading-6 text-muted-foreground">กำลังเตรียมสินค้าใหม่ใน 2 หมวด</p>
						</div>
							<Badge variant="outline" size="lg" className="h-7 w-fit gap-1.5 rounded-md border-white/15 bg-transparent px-2.5 font-normal text-white/60">
								<ClockIcon aria-hidden="true" className="size-3.5 text-white/40" />
								เร็ว ๆ นี้
							</Badge>
					</header>

					<div className="grid gap-4 xl:grid-cols-2 xl:gap-6">
						{[
							{ title: "สคริปต์พื้นฐาน", label: "Basic Script", mark: "BASIC" },
							{ title: "อีเวนต์และเกม", label: "Events & Games", mark: "EVENTS" },
						].map((category) => (
							<article key={category.label} className="relative isolate min-h-56 overflow-hidden rounded-2xl border border-white/10 bg-card shadow-[0_18px_50px_rgb(0_0_0/0.16)] sm:min-h-64">
								<img src={productPlaceholder} alt="" aria-hidden="true" className="absolute inset-y-0 right-0 h-full w-3/5 object-cover object-right opacity-45" decoding="async" fetchPriority="low" height="720" loading="lazy" width="1280" />
								<div aria-hidden="true" className="absolute inset-0 bg-gradient-to-r from-card from-35% via-card/90 via-60% to-card/25" />
								<span aria-hidden="true" className="absolute -bottom-3 -right-2 text-[clamp(4.75rem,9vw,8rem)] font-bold leading-none tracking-[-0.04em] text-white/[0.035]">{category.mark}</span>
								<div className="relative flex min-h-56 flex-col justify-between p-6 sm:min-h-64 sm:p-8">
									<p className="text-sm font-medium text-white/55">{category.label}</p>
									<div className="max-w-sm">
										<h3 className="text-balance text-2xl font-bold leading-8 sm:text-3xl sm:leading-10">{category.title}</h3>
										<p className="mt-2 text-pretty text-base leading-7 text-white/70">กำลังเตรียมสินค้าในหมวดนี้</p>
									</div>
								</div>
							</article>
						))}
					</div>

					<div className="mt-10 border-y border-white/10 py-5 sm:mt-12 sm:py-6">
						<div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between lg:gap-8">
							<header className="shrink-0">
								<h2 id="service-benefits-title" className="text-balance text-lg font-bold sm:text-xl">มาตรฐาน Errorism</h2>
								<p className="mt-0.5 text-pretty text-sm text-muted-foreground">พร้อมในทุกสคริปต์</p>
							</header>

							<ul aria-labelledby="service-benefits-title" className="grid list-none grid-cols-2 gap-x-5 gap-y-4 sm:grid-cols-4 lg:w-full lg:max-w-3xl lg:gap-6">
								{SERVICES.map((data) => (
									<li key={data.title} className="flex min-w-0 items-center gap-2.5">
										<img src={`/img/icons/${data.icon}`} className="size-5 shrink-0" alt="" aria-hidden="true" height="20" width="20" />
										<span className="text-sm font-semibold leading-5">{data.title}</span>
									</li>
								))}
							</ul>
						</div>
					</div>
				</div>
			</section>

			<section aria-labelledby="developers-title" className="px-4 pb-24 pt-2 sm:pb-32 sm:pt-6">
				<div className="container-wrapper">
					<div className="flex flex-col gap-6 sm:gap-8">
						<div className="border-b border-white/10 pb-4">
							<h2 id="developers-title" className="text-balance text-xl font-bold tracking-[-0.01em] sm:text-2xl">ทีมผู้พัฒนา</h2>
						</div>
						<div className="grid w-full grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4 lg:gap-5 xl:gap-6">
							{DEVELOPERS.map((data) => (
								<DeferredDeveloperCard key={data.name} avatar={data.avatar} name={data.name} />
							))}
						</div>
					</div>
				</div>
			</section>
		</div>
	)
}

export default App
